/**
 * Choosing, applying and remembering the theme.
 *
 * The theme is always an explicit attribute on the root element, never left to
 * the media query alone. Dark is where it starts — every approved terminal
 * mock is dark — and a stored choice overrides it — otherwise a person who picked
 * light on a machine set to dark would see their choice ignored on the next
 * visit, which reads as the setting being broken.
 */

export const THEMES = ["light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_ATTRIBUTE = "data-theme";
export const THEME_STORAGE_KEY = "skryptos.theme";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

/** The stored choice if there is one, and dark if not. */
export function resolveTheme(stored: Theme | null): Theme {
  return stored ?? "dark";
}

/**
 * Read the remembered choice.
 *
 * Storage can throw — a browser with cookies blocked does — and a theme is
 * never worth failing a page load over, so an unreadable store means "no
 * choice recorded" rather than an error.
 */
export function readStoredTheme(storage: Pick<Storage, "getItem">): Theme | null {
  try {
    const value = storage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

/** Remember a choice, or carry on silently when the store refuses. */
export function storeTheme(
  theme: Theme,
  storage: Pick<Storage, "setItem">,
): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // A theme that cannot be remembered still works for this visit.
  }
}

/** Put the theme where the stylesheet can see it. */
export function applyTheme(theme: Theme, root: Element): void {
  root.setAttribute(THEME_ATTRIBUTE, theme);
}

/** The other one. */
export function nextTheme(theme: Theme): Theme {
  return theme === "dark" ? "light" : "dark";
}
